import { Button } from './button';
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from './icons';
import { Select } from './select';
import { cn } from './utils';

export interface PaginationProps {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange?: (pageSize: number) => void;
  readonly pageSizeOptions?: readonly number[];
  readonly className?: string;
  /** Nome do que está sendo paginado, no plural. Ex.: "documentos". */
  readonly itemNoun?: string;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100, 200] as const;

/**
 * Paginação.
 *
 * ACESSIBILIDADE:
 *  · `<nav aria-label="Paginação">` agrupa os controles;
 *  · o resumo "Exibindo X–Y de Z" fica num `aria-live="polite"`, para que a
 *    troca de página seja anunciada — sem isso, quem usa leitor de tela clica
 *    "Próxima" e não recebe confirmação de nada;
 *  · os botões de seta têm rótulo textual explícito, não só ícone.
 *
 * O legado não tinha paginação nenhuma na Lista Mestra (ged.md §14.1: retornava
 * todos os registros com `ORDER BY tipo` fixo).
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  className,
  itemNoun = 'registros',
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <nav
      aria-label="Paginação"
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2.5',
        className,
      )}
    >
      <p aria-live="polite" className="text-sm text-content-muted qc-tabular">
        {total === 0
          ? `Nenhum registro`
          : `Exibindo ${first}–${last} de ${total} ${itemNoun}`}
        <span className="qc-sr-only">. Página {page} de {totalPages}.</span>
      </p>

      <div className="flex flex-wrap items-center gap-3">
        {onPageSizeChange ? (
          <div className="flex items-center gap-2">
            {/* Texto visível como contexto; o nome acessível do controle vem do
                `ariaLabel`, que é completo ("Registros por página"). */}
            <span aria-hidden="true" className="text-sm text-content-muted">
              Por página
            </span>
            <Select
              size="sm"
              ariaLabel="Registros por página"
              value={String(pageSize)}
              onValueChange={(next) => onPageSizeChange(Number(next))}
              options={pageSizeOptions.map((size) => ({
                value: String(size),
                label: String(size),
              }))}
              className="w-20"
            />
          </div>
        ) : null}

        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            iconLabel="Primeira página"
            disabled={page <= 1}
            onClick={() => onPageChange(1)}
          >
            <IconChevronsLeft size={16} />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            iconLabel="Página anterior"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <IconChevronLeft size={16} />
          </Button>

          <span className="px-2 text-sm text-content qc-tabular" aria-hidden="true">
            {page} / {totalPages}
          </span>

          <Button
            size="icon"
            variant="ghost"
            iconLabel="Próxima página"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            <IconChevronRight size={16} />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            iconLabel="Última página"
            disabled={page >= totalPages}
            onClick={() => onPageChange(totalPages)}
          >
            <IconChevronsRight size={16} />
          </Button>
        </div>
      </div>
    </nav>
  );
}
